<div class="workflow-category-list">
  <div class="card">
    <div class="card-header">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h6 class="mb-0 d-flex align-items-center">
          <i class="fas fa-folder mr-2"></i> Workflow Categories
        </h6>
        <div class="switch">
          <label class="d-flex align-items-center mb-0">
            <input
              type="checkbox"
              name="enableCategories"
              [(ngModel)]="categoriesEnabled"
              (ngModelChange)="onCategoriesEnabledChange($event)"
              [disabled]="loading"
            />
            Enable
          </label>
        </div>
      </div>
      <div class="d-flex justify-content-end" *ngIf="categoriesEnabled">
        <button
          type="button"
          class="btn btn-sm btn-primary"
          (click)="showAddForm()"
          [disabled]="showNewCategoryForm || loading"
        >
          <i class="material-icons">add</i> Add Category
        </button>
      </div>
    </div>

    <div class="card-body" *ngIf="categoriesEnabled">
      <!-- Loading Indicator -->
      <div *ngIf="loading" class="text-center py-3">
        <div class="spinner-border spinner-border-sm" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <span class="ms-2">Processing...</span>
      </div>

      <!-- New Category Form -->
      <div *ngIf="showNewCategoryForm" class="mb-3">
        <div class="card border-primary">
          <div class="card-body">
            <h6 class="card-title">Add New Category</h6>
            <form [formGroup]="newCategoryForm" (ngSubmit)="addCategory()">
              <div class="mb-3">
                <label for="categoryName" class="form-label">Name *</label>
                <input
                  type="text"
                  id="categoryName"
                  class="form-control"
                  formControlName="name"
                  placeholder="Enter category name"
                  [class.is-invalid]="
                    newCategoryForm.get('name')?.invalid &&
                    newCategoryForm.get('name')?.touched
                  "
                />
                <div
                  class="invalid-feedback"
                  *ngIf="
                    newCategoryForm.get('name')?.invalid &&
                    newCategoryForm.get('name')?.touched
                  "
                >
                  <div
                    *ngIf="newCategoryForm.get('name')?.errors?.['required']"
                  >
                    Category name is required.
                  </div>
                  <div
                    *ngIf="newCategoryForm.get('name')?.errors?.['maxlength']"
                  >
                    Category name cannot exceed 100 characters.
                  </div>
                </div>
              </div>

              <div class="d-flex gap-3">
                <button
                  type="submit"
                  class="btn btn-raised btn-success save-btn"
                  [disabled]="!newCategoryForm.valid || loading"
                >
                  Save Category
                </button>
                <button
                  type="button"
                  class="btn btn-raised btn-secondary"
                  (click)="cancelAdd()"
                  [disabled]="loading"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- Categories List -->
      <div
        *ngIf="categories.length === 0 && !showNewCategoryForm"
        class="text-center py-4"
      >
        <div class="text-muted">
          <i class="fas fa-folder-open fa-3x mb-3"></i>
          <p class="mb-0">No workflow categories found.</p>
          <p class="small">
            Click "Add Category" to create your first category.
          </p>
        </div>
      </div>

      <div
        *ngIf="categories.length > 0"
        class="categories-list"
        cdkDropList
        (cdkDropListDropped)="onDrop($event)"
      >
        <div
          *ngFor="let category of categories; trackBy: trackByFn"
          class="category-item mb-3"
          cdkDrag
          [cdkDragDisabled]="dragDisabled(category)"
        >
          <div class="card" *ngIf="category">
            <div class="card-body">
              <!-- View Mode -->
              <div *ngIf="category && !category.isEditing">
                <div class="d-flex justify-content-between align-items-start">
                  <div class="d-flex align-items-start flex-grow-1">
                    <div
                      class="drag-handle me-2"
                      cdkDragHandle
                      title="Drag to reorder"
                      style="cursor: move;"
                    >
                      <i class="material-icons text-muted">drag_indicator</i>
                    </div>
                    <div class="flex-grow-1">
                      <h6 class="mb-1">
                        <i class="fas fa-folder text-primary"></i>
                        {{ category?.name }}
                        <span
                          *ngIf="category?.isStatic"
                          class="badge bg-secondary ms-2"
                          >System</span
                        >
                      </h6>
                    </div>
                  </div>
                  <div
                    class="d-flex align-items-center"
                    *ngIf="!category?.isStatic"
                    (mousedown)="$event.stopPropagation()"
                    (touchstart)="$event.stopPropagation()"
                  >
                    <button
                      type="button"
                      class="btn btn-sm btn-primary mr-2"
                      (click)="editCategory(category)"
                      [disabled]="loading"
                      title="Edit Category"
                    >
                      Edit
                    </button>
                    <wm-modal-confirm
                      [showCommandText]="true"
                      [commandText]="'Yes'"
                      [cancelText]="'No'"
                      [showButtonText]="true"
                      [showButton]="true"
                      [btnIcon]="'delete'"
                      [title]="'Remove Category'"
                      [buttonClass]="'btn btn-sm'"
                      (accepted)="deleteCategory(category)"
                    >
                      <p>
                        Are you sure you want to delete this category ({{
                          category.name
                        }})?
                      </p>
                      <br />
                      <p>
                        This category will be removed and workflows currently
                        assigned to this category will be unassigned.
                      </p>
                    </wm-modal-confirm>
                  </div>
                </div>
              </div>

              <!-- Edit Mode -->
              <div *ngIf="category && category.isEditing">
                <div class="mb-3">
                  <label class="form-label">Name *</label>
                  <input
                    type="text"
                    class="form-control"
                    [(ngModel)]="category.name"
                    placeholder="Enter category name"
                  />
                </div>

                <div class="d-flex gap-3">
                  <button
                    type="button"
                    class="btn btn-raised btn-primary mr-2"
                    (click)="saveCategory(category)"
                    [disabled]="!category?.name?.trim() || loading"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    class="btn btn-raised btn-secondary "
                    (click)="cancelEdit(category)"
                    [disabled]="loading"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="card-body" *ngIf="!categoriesEnabled">
      <div class="text-center py-4">
        <div class="text-muted">
          <i class="fas fa-folder-open fa-3x mb-3"></i>
          <p class="mb-0">Categories are currently disabled.</p>
          <p class="small">
            Enable categories using the toggle above to start organizing your
            workflow.
          </p>
        </div>
      </div>
    </div>
  </div>
</div>

<wm-modal-confirm
  #disableCategoriesModal
  [showButtonText]="true"
  [showButton]="false"
  [buttonText]="''"
  commandText="Yes, Disable"
  title="Disable Workflow Categories"
  (accepted)="confirmDisableCategories()"
  (cancelled)="cancelDisableCategories()"
  btnColor="danger"
  [closeOnAccept]="false"
>
  Are you sure you want to disable workflow categories? All custom categories
  will be removed from this workflow. This action cannot be undone.
</wm-modal-confirm>
